import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, empty } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { ApiService } from './api.service';
import { BaseResponse } from '../models';

export interface OrderFee {
  id: number;
  type: string;
  feeMode: 'Fixed' | 'Variable';
  minValue?: number | null;
  minQty?: number | null;
  fixedFee?: number | null;
  currency: string;          
  regions: string[];
  startDate?: string | null;
  message?: string | null;
  messageFr?: string | null;
  active: boolean;
}

export interface OrderFeeGetDto {
  orderFeesId: number;
  orderFeesType: string;
  orderFeesCategory?: string | null;
  minimumValue?: number | null;
  minimumQuantity?: number | null;
  fixedFee?: number | null;
  currencyCode: string;      
  regionCode?: string | null;
  startDate?: string | null;
  orderFeesMessage?: string | null;
  orderFeesMessageEng?: string | null;
  orderFeesMessageFre?: string | null;
  isActive: boolean;
}

export interface OrderFeeRequestResource {
  OrderFeesId: number;
  OrderFeesType: string;
  OrderFeesCategory: string;
  MinimumValue: number;
  MinimumQuantity: number;
  FixedFee: number;
  CurrencyCode: string;
  RegionCode: string;
  StartDate: string;
  OrderFeesMessageEng: string;
  OrderFeesMessageFre: string;
  IsActive: boolean;
}

export interface RegionCurrencyPairDto {
  currencyCode: string;
  regionCode: string;
}
export interface RegionCurrencyResponse extends BaseResponse {
  data: RegionCurrencyPairDto[];
}

@Injectable({ providedIn: 'root' })
export class OrderFeesService {
  private readonly getAllUrl = '/OrderFees/GetAllOrderFees';
  private readonly addUrl = '/OrderFees/AddNewOrderFee';
  private readonly updateUrl = '/OrderFees/UpdateOrderFee';
  private readonly deleteUrl = '/OrderFees/DeleteOrderFee';
  private readonly getCodesUrl = '/OrderFees/GetAllRegionAndCurrencyCodes';

  constructor(private httpClient: HttpClient) { }

  listOrderFee(state: State): Observable<GridDataResult> {
    return ApiService.genericObservableGetKd(this.httpClient, this.getAllUrl).pipe(
      catchError(function (err, caught) { console.log(err); console.log(caught); return empty(); }),
      map(function (resp: BaseResponse) {
        const rows: OrderFeeGetDto[] = resp && (resp as any).data ? (resp as any).data as OrderFeeGetDto[] : [];
        const mapped: OrderFee[] = rows.map(OrderFeesService.prototype.mapFromGetDtoToOrderFee);
        const total = mapped.length;

        var skip = (state && typeof state.skip === 'number') ? state.skip : 0;
        var take = (state && typeof state.take === 'number') ? state.take : 50;

        return { data: mapped.slice(skip, skip + take), total: total };
      })
    );
  }

  createOrderFee(ui: any): Observable<any> {
    const body: OrderFeeRequestResource = this.mapFromUiToRequest(ui);
    return ApiService.genericObservablePostKd(this.httpClient, this.addUrl, body).pipe(
      catchError(function (err, caught) { console.log(err); console.log(caught); return empty(); }),
      map(function (response: BaseResponse) { return ((response as any) && (response as any).data); })
    );
  }

  updateOrderFee(ui: any): Observable<any> {
    const body: OrderFeeRequestResource = this.mapFromUiToRequest(ui);
    return ApiService.genericObservablePostKd(this.httpClient, this.updateUrl, body).pipe(
      catchError(function (err, caught) { console.log(err); console.log(caught); return empty(); }),
      map(function (response: BaseResponse) { return ((response as any) && (response as any).data); })
    );
  }

  removeOrderFee(ui: any): Observable<any> {
    const body: OrderFeeRequestResource = this.mapFromUiToRequest(ui);
    return ApiService.genericObservablePostKd(this.httpClient, this.deleteUrl, body).pipe(
      catchError(function (err, caught) { console.log(err); console.log(caught); return empty(); }),
      map(function (response: BaseResponse) { return ((response as any) && (response as any).data); })
    );
  }

  private mapFromGetDtoToOrderFee(api: OrderFeeGetDto): OrderFee {
    const modeRaw = (api.orderFeesCategory || '').toString();
    const mode: 'Fixed' | 'Variable' = (modeRaw === 'Variable') ? 'Variable' : 'Fixed';

    const eng = (api.orderFeesMessageEng != null && api.orderFeesMessageEng !== '')
      ? api.orderFeesMessageEng
      : ((api.orderFeesMessage != null && api.orderFeesMessage !== '') ? api.orderFeesMessage : null);

    const fre = (api.orderFeesMessageFre != null && api.orderFeesMessageFre !== '')
      ? api.orderFeesMessageFre
      : null;

    const regions = (api.regionCode || '')
      .split(/[,|]/)
      .map(function (s) { return s.trim(); })
      .filter(function (x) { return !!x; });

    return {
      id: api.orderFeesId,
      type: api.orderFeesType,
      feeMode: mode,
      minValue: (api.minimumValue === undefined || api.minimumValue === null) ? null : api.minimumValue,
      minQty: (api.minimumQuantity === undefined || api.minimumQuantity === null) ? null : api.minimumQuantity,
      fixedFee: (api.fixedFee === undefined || api.fixedFee === null) ? null : api.fixedFee,
      currency: api.currencyCode,   
      regions: regions,
      startDate: (api.startDate === undefined || api.startDate === null) ? null : api.startDate.substring(0, 10),
      message: eng,
      messageFr: fre,
      active: !!api.isActive
    };
  }

  private mapFromUiToRequest(ui: any): OrderFeeRequestResource {
    return {
      OrderFeesId: (ui.id === undefined || ui.id === null) ? 0 : ui.id,
      OrderFeesType: ui.type,
      OrderFeesCategory: ui.feeMode || 'Fixed',
      MinimumValue: (ui.minValue === undefined || ui.minValue === null) ? 0 : ui.minValue,
      MinimumQuantity: (ui.minQty === undefined || ui.minQty === null) ? 0 : Math.trunc(ui.minQty),
      FixedFee: (ui.fixedFee === undefined || ui.fixedFee === null) ? 0 : ui.fixedFee,
      CurrencyCode: ui.currency,
      RegionCode: ui.regionCsv || '',
      StartDate: ui.startDate || '',
      OrderFeesMessageEng: (ui.message === undefined || ui.message === null) ? '' : ui.message,
      OrderFeesMessageFre: (ui.messageFr === undefined || ui.messageFr === null) ? '' : ui.messageFr,
      IsActive: !!ui.active
    };
  }

  getRegionAndCurrencyCodes(): Observable<{ currencies: string[]; map: Record<string, string[]>; }> {
    return ApiService.genericObservableGetKd(this.httpClient, this.getCodesUrl).pipe(
      catchError(function (err, caught) { console.log(err); console.log(caught); return empty(); }),
      map(function (resp: RegionCurrencyResponse) {
        const pairs: RegionCurrencyPairDto[] = resp && (resp as any).data ? (resp as any).data as RegionCurrencyPairDto[] : [];

        const mapByCurrency: Record<string, string[]> = {};
        for (var i = 0; i < pairs.length; i++) {
          var c = (pairs[i].currencyCode || '').toString().trim();
          var r = (pairs[i].regionCode || '').toString().trim();
          if (!c || !r) { continue; }
          if (!mapByCurrency[c]) { mapByCurrency[c] = []; }
          mapByCurrency[c].push(r);
        }

        const currencies = Object.keys(mapByCurrency).sort();
        for (var k = 0; k < currencies.length; k++) {
          var cur = currencies[k];
          var arr = mapByCurrency[cur] || [];
          var seen: { [s: string]: boolean } = {};
          var dedup: string[] = [];
          for (var j = 0; j < arr.length; j++) {
            var val = arr[j];
            if (!seen[val]) { seen[val] = true; dedup.push(val); }
          }
          dedup.sort();
          mapByCurrency[cur] = dedup;
        }

        return { currencies: currencies, map: mapByCurrency };
      })
    );
  }
}
